import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Description as DescriptionIcon,
  CloudDownload as CloudDownloadIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  Science as ScienceIcon,
  AccountBalance as AccountBalanceIcon,
  Verified as VerifiedIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { VERSION_INFO } from '../constants/version';

interface SettingsProps {
  analysisResult?: any;
}

const Settings: React.FC<SettingsProps> = ({ analysisResult }) => {
  const [activeTab, setActiveTab] = useState('mappings');
  const [researchData, setResearchData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [pricingData, setPricingData] = useState<any>(null);

  const sampleBills = [
    {
      id: 'startup-company',
      name: 'Startup Company',
      cost: '$847/month',
      description: 'Small development team with basic compute and storage',
      services: ['EC2 t3.medium (2)', 'S3 Standard (100GB)', 'RDS MySQL t3.micro', 'CloudWatch'],
      icon: <BusinessIcon />,
      csvFile: 'startup-company.csv'
    },
    {
      id: 'enterprise-corporation',
      name: 'Enterprise Corporation',
      cost: '$12.7K/month',
      description: 'Mid-size company with production workloads and high availability',
      services: ['EC2 m5.xlarge (10)', 'S3 + CloudFront (2TB)', 'RDS Multi-AZ', 'Load Balancers', 'VPC'],
      icon: <AccountBalanceIcon />,
      csvFile: 'enterprise-corporation.csv'
    },
    {
      id: 'ml-workload',
      name: 'ML/AI Workload',
      cost: '$8.9K/month',
      description: 'Machine learning pipeline with GPU compute and data processing',
      services: ['p3.2xlarge GPU (4)', 'SageMaker', 'S3 + Glacier (10TB)', 'EMR Cluster'],
      icon: <PsychologyIcon />,
      csvFile: 'ml-workload.csv'
    },
    {
      id: 'super-enterprise',
      name: 'Super Enterprise',
      cost: '$2.54M/month',
      description: 'Large-scale enterprise with global infrastructure and complex workloads',
      services: ['1000+ EC2 instances', 'Multi-region S3 (500TB)', 'RDS Aurora clusters', 'CloudFront global'],
      icon: <ScienceIcon />,
      csvFile: 'super-enterprise.csv'
    }
  ];

  useEffect(() => {
    if (activeTab === 'mappings') {
      fetchResearchData();
    } else if (activeTab === 'pricing') {
      fetchPricingValidationData();
    }
  }, [activeTab]);

  const fetchResearchData = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/research-data`);
      if (response.ok) {
        const data = await response.json();
        setResearchData(data);
      }
    } catch (error) {
      console.error('Failed to fetch research data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingValidationData = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/research-data`);
      if (response.ok) {
        const data = await response.json();
        setPricingData(data?.metadata || null);
      }
    } catch (error) {
      console.error('Failed to fetch pricing validation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadMasterJSON = () => {
    if (researchData) {
      const blob = new Blob([JSON.stringify(researchData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aws-oci-master-mappings.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const downloadSampleBillCSV = async (csvFile: string, displayName: string) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/sample-bills/${csvFile}`);
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sample-${csvFile}`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download CSV:', error);
    }
  };

  const downloadPricingJSON = async (provider: 'aws' | 'oci') => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/pricing-data/${provider}`);
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${provider}-pricing-data.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(`Failed to download ${provider.toUpperCase()} pricing JSON:`, error);
    }
  };

  const renderMappingsTab = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Master Mappings File:</strong> All AWS to OCI service mappings, pricing data, and research content 
          are now centralized in a single JSON file. This is the primary data source used by both the Research page 
          and the bill analysis system.
        </Typography>
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <StorageIcon color="primary" />
              <Typography variant="h6">
                AWS-OCI Master Mappings
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadMasterJSON}
              disabled={!researchData || loading}
            >
              Download JSON
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>File:</strong> <code>backend/data/aws_oci_research.json</code>
          </Typography>
          
          {researchData && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Contents:</Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Service Mappings" 
                    secondary={`${Object.keys(researchData.aws_to_oci_service_mappings || {}).length} categories with comprehensive AWS → OCI mappings`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Instance Mappings" 
                    secondary={`${Object.keys(researchData.ec2_to_oci_shape_mappings || {}).length} categories with EC2 → OCI compute shape mappings`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Pricing Data" 
                    secondary="OCI pricing information and cost adjustment factors"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Research Content" 
                    secondary={`${Object.keys(researchData.enterprise_compute_families || {}).length} compute families with detailed specifications`}
                  />
                </ListItem>
              </List>
            </Box>
          )}

          {loading && (
            <Typography variant="body2" color="text.secondary">
              Loading mapping data...
            </Typography>
          )}
        </CardContent>
      </Card>

      {analysisResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="secondary" />
              Current Analysis Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This is the analysis result from your uploaded bill, using the master mappings above.
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'grey.300',
              maxHeight: 400,
              overflow: 'auto'
            }}>
              <Typography 
                component="pre" 
                sx={{ 
                  fontSize: '0.875rem', 
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {JSON.stringify(analysisResult, null, 2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderSampleBillsTab = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Real CSV Data:</strong> These example bills are now stored as actual CSV files in the backend 
          (<code>backend/data/sample-bills/</code>) instead of being hardcoded. You can download and examine 
          the raw data used for demonstrations.
        </Typography>
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {sampleBills.map((bill) => (
          <Card key={bill.id} sx={{ position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {bill.icon}
                  <Box>
                    <Typography variant="h6" component="h3">
                      {bill.name}
                    </Typography>
                    <Chip 
                      label={bill.cost} 
                      color="primary" 
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => downloadSampleBillCSV(bill.csvFile, bill.name)}
                  title="Download CSV"
                >
                  <CloudDownloadIcon />
                </IconButton>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {bill.description}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Key Services:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {bill.services.slice(0, 3).map((service, index) => (
                  <Chip 
                    key={index}
                    label={service} 
                    size="small" 
                    variant="outlined"
                  />
                ))}
                {bill.services.length > 3 && (
                  <Chip 
                    label={`+${bill.services.length - 3} more`}
                    size="small" 
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">
                <strong>File:</strong> <code>{bill.csvFile}</code>
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  const renderPricingValidationTab = () => (
    <Box>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Pricing Data Architecture:</strong> We maintain separate AWS and OCI pricing JSON files that are regularly validated 
          against the master mappings file to ensure accuracy and consistency across all cost calculations.
        </Typography>
      </Alert>

      {/* Pricing Data Files */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon color="primary" />
            Standalone Pricing Data Files
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Independent pricing files following AWS pricing schema format for easy maintenance and updates.
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {/* AWS Pricing */}
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="AWS" color="warning" size="small" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      AWS Pricing Data
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadPricingJSON('aws')}
                  >
                    Download
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>File:</strong> <code>backend/data/pricing-data/aws-pricing.json</code>
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Format" 
                      secondary="AWS Price List API compatible schema"
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Coverage" 
                      secondary="EC2 instances, Storage, Networking services"
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Last Updated" 
                      secondary="2025-07-28 (Tier 1 verified pricing)"
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* OCI Pricing */}
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="OCI" color="success" size="small" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      OCI Pricing Data
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadPricingJSON('oci')}
                  >
                    Download
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>File:</strong> <code>backend/data/pricing-data/oci-pricing.json</code>
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Format" 
                      secondary="AWS-compatible schema for consistency"
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Coverage" 
                      secondary="Compute shapes, Block volumes, Object storage"
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Last Updated" 
                      secondary="2025-07-28 (Oracle official pricing verified)"
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Card>

      {/* Validation Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedIcon color="success" />
            Pricing Accuracy & Validation
          </Typography>
          
          {pricingData && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Current Version:</strong> {pricingData.version} | 
                  <strong> Last Updated:</strong> {pricingData.last_updated} | 
                  <strong> Pricing Date:</strong> {pricingData.pricing_date}
                </Typography>
              </Alert>

              <Typography variant="subtitle2" gutterBottom>Recent Updates:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pricingData.update_notes}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>Data Sources:</Typography>
              <List dense>
                {pricingData.sources?.slice(0, 4).map((source: string, index: number) => (
                  <ListItem key={index} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <VerifiedIcon color="primary" sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={source.split(' (')[0]} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
                {pricingData.sources?.length > 4 && (
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary={`+${pricingData.sources.length - 4} more verified sources`}
                      primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic', color: 'text.secondary' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Validation Scripts & Reports */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="secondary" />
            Validation Scripts & Reports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Automated validation ensures pricing consistency between standalone files and master mappings.
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Validation Script
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>File:</strong> <code>backend/data/pricing-data/pricing-validation-script.py</code>
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary="Cross-validates AWS and OCI pricing files"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary="Compares against master mappings JSON"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary="Generates detailed discrepancy reports"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Latest Validation Reports
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Location:</strong> <code>backend/data/pricing-data/validation-reports/</code>
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary="pricing_validation_report_2025-07-28_05-20-20.md"
                      secondary="Latest comprehensive validation report"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary="All critical pricing discrepancies resolved"
                      secondary="VM.Standard.E5.Flex, E4.Flex pricing corrected"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon />
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          View and manage application settings, mapping configurations, and sample data.
        </Typography>

        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="Master Mappings" 
            value="mappings" 
            icon={<StorageIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Pricing Validation" 
            value="pricing" 
            icon={<VerifiedIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Sample Bills" 
            value="samples" 
            icon={<DescriptionIcon />} 
            iconPosition="start"
          />
        </Tabs>

        {activeTab === 'mappings' && renderMappingsTab()}
        {activeTab === 'pricing' && renderPricingValidationTab()}
        {activeTab === 'samples' && renderSampleBillsTab()}

        <Divider sx={{ my: 4 }} />
        
        {/* App Info */}
        <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} color="primary.main">
            Alfred Cloud Compare {VERSION_INFO}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AWS to Oracle Cloud Infrastructure Migration Analysis Tool
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Settings;